import { axiosClient } from '../network/appClient';

export interface HospitalityReservationProductDTO {
    id: number;
    name: string;
    date: string;
    schedule: string;
    token: string;
    value?: number;
    discountValue?: number;
}

/** Campos retornados pelo endpoint de detalhe: GET `Hospitality/Reservation?Token=…` */
export interface HospitalityReservationResponseDTO {
    id: number;
    token: string;
    statusId: number;
    checkIn: string;
    checkOut: string;
    products: HospitalityReservationProductDTO[];
    scheduledFreeBayWaves: number;
    scheduledFreeReefWaves: number;
    /** Presente quando o backend envia cota de ondas pagas. */
    scheduledPaidBayWaves?: number;
    scheduledPaidReefWaves?: number;
}

/**
 * Campos retornados pelo endpoint de listagem: GET `Hospitality/Reservation/List`.
 * Não inclui `products` nem `scheduledFree*` — esses campos só vêm no detalhe por token.
 */
export interface HospitalityReservationListItemDTO {
    id: number;
    /** Presente na listagem quando o backend envia vínculo com contrato. */
    contractId?: number;
    /** Tipo da reserva (regra de negócio atual: exibir apenas `type = 1`). */
    type?: number;
    token: string;
    statusId: number;
    checkIn: string;
    checkOut: string;
    scheduledPaidBayWaves?: number;
    scheduledPaidReefWaves?: number;
    scheduledFreeBayWaves?: number;
    scheduledFreeReefWaves?: number;
}

/**
 * Query padrão para GET `Hospitality/Reservation/List`.
 * Alinhar chaves ao Swagger da API se o binding esperar outros nomes (ex.: `IncludeSchedulePaidWaves`).
 */
export const hospitalityReservationListQueryDefaults = {
    StatusId: 4,
    Type: 1,
    SchedulePaidWaves: false,
    FreeWaves: false,
} as const;

function isReservationListItem(value: unknown): value is HospitalityReservationListItemDTO {
    if (!value || typeof value !== 'object') return false;
    const o = value as Record<string, unknown>;
    return (
        typeof o.id === 'number' &&
        typeof o.token === 'string' &&
        typeof o.checkIn === 'string' &&
        typeof o.checkOut === 'string' &&
        typeof o.statusId === 'number'
    );
}

function normalizeReservationListPayload(data: unknown): HospitalityReservationListItemDTO[] {
    if (Array.isArray(data)) {
        return data.filter(isReservationListItem);
    }
    if (data && typeof data === 'object') {
        if (isReservationListItem(data)) {
            return [data];
        }
        const obj = data as Record<string, unknown>;
        for (const key of ['items', 'data', 'reservations', 'value'] as const) {
            const arr = obj[key];
            if (Array.isArray(arr)) {
                return arr.filter(isReservationListItem);
            }
        }
    }
    return [];
}

const mapReservationListError = (status: number, backendMessage?: string): string => {
    if (status === 401) return 'Faça login novamente.';
    if (status === 403) return 'Você não tem permissão para listar estas reservas.';
    return backendMessage || 'Não foi possível carregar as reservas.';
};

const extractBackendMessage = (data: unknown): string | undefined => {
    if (!data || typeof data !== 'object') return undefined;

    const obj = data as Record<string, unknown>;
    const directMessage =
        (obj.detail as string | undefined) ||
        (obj.message as string | undefined) ||
        (obj.parameter as string | undefined) ||
        (obj.title as string | undefined);

    if (directMessage) return directMessage;

    const errors = obj.errors as Record<string, unknown> | undefined;
    if (!errors || typeof errors !== 'object') return undefined;

    const firstErrorValue = Object.values(errors)[0];
    if (Array.isArray(firstErrorValue) && typeof firstErrorValue[0] === 'string') {
        return firstErrorValue[0];
    }

    return undefined;
};

const isWrongUserError = (rawMessage: string) => {
    const normalized = rawMessage.toLowerCase();
    return (
        normalized.includes('notowner') ||
        normalized.includes('not owner') ||
        normalized.includes('does not belong') ||
        normalized.includes('personid') ||
        normalized.includes('different person') ||
        normalized.includes('unauthorized') ||
        normalized.includes('forbidden')
    );
};

const mapReservationError = (status: number, backendMessage?: string): string => {
    if (status === 401) return 'Faça login novamente.';
    if (status === 404) {
        return 'Reserva não encontrada. Verifique se você está logado com o usuário da reserva.';
    }

    if (backendMessage && isWrongUserError(backendMessage)) {
        return 'Essa reserva não pertence ao usuário logado.';
    }

    if (status === 400) {
        return 'Link de reserva inválido.';
    }

    if (status === 403) {
        return 'Essa reserva não pertence ao usuário logado.';
    }

    return backendMessage || 'Não foi possível validar a reserva.';
};

export const getHospitalityReservationByToken = async (
    reservationToken: string
): Promise<{
    reservation?: HospitalityReservationResponseDTO;
    error?: string;
}> => {
    try {
        const response = await axiosClient.get('Hospitality/Reservation', {
            params: { Token: reservationToken },
            validateStatus: () => true,
        });

        if (response.status === 200) {
            return {
                reservation: response.data as HospitalityReservationResponseDTO,
            };
        }

        const backendMessage = extractBackendMessage(response.data);
        return { error: mapReservationError(response.status, backendMessage) };
    } catch {
        return { error: 'Não foi possível validar a reserva.' };
    }
};

export const getHospitalityReservationList = async (): Promise<{
    items?: HospitalityReservationListItemDTO[];
    error?: string;
}> => {
    try {
        const response = await axiosClient.get('Hospitality/Reservation/List', {
            params: { ...hospitalityReservationListQueryDefaults },
            validateStatus: () => true,
        });

        if (response.status === 200) {
            return { items: normalizeReservationListPayload(response.data) };
        }

        const backendMessage = extractBackendMessage(response.data);
        return { error: mapReservationListError(response.status, backendMessage) };
    } catch {
        return { error: 'Não foi possível carregar as reservas.' };
    }
};
